import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BookOpenIcon } from '@heroicons/react/24/outline';
import PlaybookCard from './PlaybookCard';
import PlaybookFilterBar from './PlaybookFilterBar';
import PlaybookDetailView from './PlaybookDetailView';
import PlaybookHandbook from './handbook/PlaybookHandbook';
import { usePlaybookLibrary } from '../hooks/usePlaybookLibrary';
import { PLAYBOOK_KEYFRAMES } from '../types/playbook.types';
import type { PlaybookEntity, PlaybookListFilters } from '../types/playbook.types';

interface PlaybookGroup {
  system: PlaybookEntity;
  variants: PlaybookEntity[];
}

function groupPlaybooks(playbooks: PlaybookEntity[]): { groups: PlaybookGroup[]; standalone: PlaybookEntity[] } {
  const systemMap = new Map<string, PlaybookEntity>();
  const variantsBySource = new Map<string, PlaybookEntity[]>();
  const standalone: PlaybookEntity[] = [];

  for (const pb of playbooks) {
    if (pb.is_system) {
      systemMap.set(pb.id, pb);
    } else if (pb.source_playbook_id) {
      const arr = variantsBySource.get(pb.source_playbook_id) || [];
      arr.push(pb);
      variantsBySource.set(pb.source_playbook_id, arr);
    } else {
      standalone.push(pb);
    }
  }

  const groups: PlaybookGroup[] = [];
  for (const [sysId, sys] of systemMap) {
    groups.push({ system: sys, variants: variantsBySource.get(sysId) || [] });
    variantsBySource.delete(sysId);
  }

  // Orphaned variants (source not in current page)
  for (const variants of variantsBySource.values()) {
    standalone.push(...variants);
  }

  return { groups, standalone };
}

const PlaybookLibraryPanel: React.FC = () => {
  const { playbooks, total, loading, error, upstreamMap, load, clonePlaybook, cloneSystemPlaybook, publishPlaybook, archivePlaybook } = usePlaybookLibrary();
  const [filters, setFilters] = useState<PlaybookListFilters>({ page: 1, limit: 50 });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showHandbook, setShowHandbook] = useState(false);

  useEffect(() => {
    load(filters);
  }, [load, filters]);

  const updateFilter = useCallback((key: keyof PlaybookListFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined, page: 1 }));
  }, []);

  const { groups, standalone } = useMemo(() => groupPlaybooks(playbooks), [playbooks]);

  const handleClone = useCallback(async (id: string) => {
    const pb = playbooks.find((p) => p.id === id);
    try {
      if (pb?.is_system) {
        await cloneSystemPlaybook(id);
      } else {
        await clonePlaybook(id);
      }
      load(filters);
    } catch (err) {
      console.error('[PlaybookLibrary] Clone failed:', err);
    }
  }, [playbooks, clonePlaybook, cloneSystemPlaybook, load, filters]);

  const handlePublish = useCallback(async (id: string) => {
    try {
      await publishPlaybook(id);
      load(filters);
    } catch (err) {
      console.error('[PlaybookLibrary] Publish failed:', err);
    }
  }, [publishPlaybook, load, filters]);

  const handleArchive = useCallback(async (id: string) => {
    if (!window.confirm('Are you sure you want to archive this playbook?')) return;
    try {
      await archivePlaybook(id);
      load(filters);
    } catch (err) {
      console.error('[PlaybookLibrary] Archive failed:', err);
    }
  }, [archivePlaybook, load, filters]);

  if (selectedId) {
    return (
      <PlaybookDetailView
        playbookId={selectedId}
        onBack={() => { setSelectedId(null); load(filters); }}
      />
    );
  }

  return (
    <>
      <style>{PLAYBOOK_KEYFRAMES}</style>
      <div className="space-y-4" style={{ animation: 'fadeSlideIn 300ms ease-out' }}>
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Playbook Library</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Browse, clone, and manage workflow playbooks for your firm.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowHandbook(true)}
            className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700"
          >
            <BookOpenIcon className="w-3.5 h-3.5" />
            Handbook
          </button>
        </div>

        {/* Filters */}
        <PlaybookFilterBar
          practiceArea={filters.practice_area || ''}
          lifecycle={filters.lifecycle || ''}
          source={filters.source || ''}
          onPracticeAreaChange={(v) => updateFilter('practice_area', v)}
          onLifecycleChange={(v) => updateFilter('lifecycle', v)}
          onSourceChange={(v) => updateFilter('source', v)}
        />

        {/* Loading */}
        {loading && (
          <div className="flex items-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-lex-accent border-t-transparent" />
            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading playbooks...</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
            {error}
          </div>
        )}

        {/* Results */}
        {!loading && !error && (
          <>
            <div className="text-xs text-gray-500 dark:text-gray-400">{total} playbook{total !== 1 ? 's' : ''}</div>

            {/* Grouped system playbooks */}
            {groups.map((group) => (
              <div key={group.system.id} className="space-y-1.5">
                <PlaybookCard
                  playbook={group.system}
                  onView={setSelectedId}
                  onClone={handleClone}
                />
                {group.variants.length > 0 && (
                  <div className="space-y-1.5">
                    <span className="ml-6 text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                      Firm Variants ({group.variants.length})
                    </span>
                    {group.variants.map((v) => (
                      <PlaybookCard
                        key={v.id}
                        playbook={v}
                        upstream={upstreamMap[v.id]}
                        indent
                        onView={setSelectedId}
                        onClone={handleClone}
                        onPublish={handlePublish}
                        onArchive={handleArchive}
                        onUpstreamClick={(id) => setSelectedId(id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Standalone playbooks */}
            {standalone.length > 0 && groups.length > 0 && (
              <span className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                Other Playbooks ({standalone.length})
              </span>
            )}
            {standalone.map((pb) => (
              <PlaybookCard
                key={pb.id}
                playbook={pb}
                upstream={upstreamMap[pb.id]}
                onView={setSelectedId}
                onClone={handleClone}
                onPublish={handlePublish}
                onArchive={handleArchive}
                onUpstreamClick={(id) => setSelectedId(id)}
              />
            ))}

            {/* Empty state */}
            {playbooks.length === 0 && (
              <div className="text-center py-12">
                <p className="text-sm text-gray-500 dark:text-gray-400">No playbooks found.</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Adjust filters or check system playbook availability.
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {showHandbook && (
        <PlaybookHandbook
          isOpen={showHandbook}
          onClose={() => setShowHandbook(false)}
          initialChapter="library"
        />
      )}
    </>
  );
};

export default PlaybookLibraryPanel;
